import { Injectable } from '@angular/core';
import { Group, PagedResult, User } from '@core/interfaces';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UsersApi {
  constructor(private readonly apollo: Apollo) { }

  listUsers({ where, orderBy, limit, offset }): Observable<PagedResult<User>> {
    return this.apollo
      .query({
        query: gql`
          query listUsers($where: UserWhereInput, $orderBy: UserOrderByInput, $limit: Int, $offset:Int) {
            users(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                avatarUrl
                createdAt
                archived
                email
                type
                role
                mfaEnabled
                loginProvider
                loginIntegrationEnabled
                lastLoginAt
              }
            }
          }
        `,
        variables: {
          where,
          orderBy,
          limit,
          offset
        }
      })
      .pipe(map((response) => response.data['users'] as PagedResult<User>));
  }

  getUserById(id: string): Observable<User | null> {
    return this.apollo
      .query({
        query: gql`
          query getOneUser($id: ID!) {
            user(id: $id) {
              id
              name
              email
              createdAt
              archived
              avatarUrl
              type
              role
              loginIntegrationEnabled
              loginProvider
              groups {
                id
                name
                description
                archived
              }
              preferences {
                featureOpenedMaintenanceSendEmailNotification
              }
              authenticationInfo {
                enabled
                status
              }
            }
          }
        `,
        variables: {
          id
        }
      })
      .pipe(map((response) => response.data['user'] as User));
  }

  updateUser(id: string, input: any): Observable<void> {
    return this.apollo
      .query({
        query: gql`
            mutation updateUser($id: ID!, $input: UserInput!) {
              updateUser(id: $id, input: $input) {
                id
                name
                avatarUrl
                archived
                preferences {
                  featureOpenedMaintenanceSendEmailNotification
                }
              }
          }
        `,
        variables: {
          id,
          input
        }
      })
      .pipe(map((response) => response.data['updateUser'] as void));
  }

  setUserPassword(id: string, input: { password: string }): Observable<void> {
    return this.apollo
      .query({
        query: gql`
          mutation setUserPassword($id: ID!, $input: SetUserPasswordInput!) {
            setUserPassword(id: $id, input: $input) {
              id
            }
          }
        `,
        variables: {
          id,
          input
        }
      })
      .pipe(map((response) => response.data['setUserPassword'] as void));
  }

  createUser(input: any): Observable<void> {
    return this.apollo
      .query({
        query: gql`
          mutation createUser($input: UserInput!) {
            createUser(input: $input) {
              id
              name
              avatarUrl
            }
          }
        `,
        variables: {
          input
        }
      })
      .pipe(map((response) => response.data['createUser'] as void));
  }

  listAllGroups(): Observable<Group[]> {
    return this.apollo
      .query({
        query: gql`
          query listGroups {
              groups(orderBy: name_asc, limit: 100, offset: 0) {
              pageInfo {
                hasNextPage
                hasPreviousPage
              }
              totalCount
              items {
                id
                name
                description
                createdAt
              }
            }
          }
        `
      })
      .pipe(map((response) => response.data['groups']['items'] as Group[]));
  }

  archiveUser(id: string): Observable<void> {
    return this.apollo
      .query({
        query: gql`
          mutation archiveUser($id: ID!) {
          archiveUser(id: $id) {
            id
            archived
        }
      }
        `,
        variables: {
          id
        }
      })
      .pipe(map((response) => response.data['archiveUser'] as void));
  }

  unarchiveUser(id: string): Observable<void> {
    return this.apollo
      .query({
        query: gql`
          mutation unarchiveUser($id: ID!) {
          unarchiveUser(id: $id) {
            id
            archived
        }
      }
        `,
        variables: {
          id
        }
      })
      .pipe(map((response) => response.data['unarchiveUser'] as void));
  }
}
